import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { takeUntil } from 'rxjs/operators';
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import { ReservationService } from '../../../../service/reservation-service/reservation.service';
import { FacilityService } from '../../../../service/facility-service/facility.service';

@Component({
  selector: 'app-facility-rating-modal',
  templateUrl: './facility-rating-modal.component.html',
  styleUrls: ['./facility-rating-modal.component.css']
})
export class FacilityRatingModalComponent implements OnInit, OnDestroy {
  @Input() facilityId: number | string;
  @Input() referenceId: string;
  @Input() facilityCard: any;
  @Output() onCloseModal = new EventEmitter<void>();

  ratingForm: FormGroup;
  submitted = false;
  isSubmitting = false;
  isFacilityLoading = true;
  facilityErrorMessage = '';

  ratingCategories = [
    { key: 'ratingFacility', label: 'Facility' },
    { key: 'ratingCleanliness', label: 'Cleanliness' },
    { key: 'ratingValueForMoney', label: 'Value for Money' },
    { key: 'ratingRoomComfort', label: 'Room Comfort and Quality' },
    { key: 'ratingStaffCare', label: 'Staff and Care' }
  ];

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private reservationService: ReservationService,
    private facilityService: FacilityService
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.fetchFacilityDetails();
  }

  ngOnDestroy(): void { }

  initForm(): void {
    this.ratingForm = this.fb.group({
      title: ['', Validators.required],
      summary: [''],
      ratingFacility: [0, [Validators.required, Validators.min(1), Validators.max(5)]],
      ratingCleanliness: [0, [Validators.required, Validators.min(1), Validators.max(5)]],
      ratingValueForMoney: [0, [Validators.required, Validators.min(1), Validators.max(5)]],
      ratingRoomComfort: [0, [Validators.required, Validators.min(1), Validators.max(5)]],
      ratingStaffCare: [0, [Validators.required, Validators.min(1), Validators.max(5)]]
    });
  }

  fetchFacilityDetails(): void {
    if (!this.facilityId) {
      this.isFacilityLoading = false;
      this.facilityErrorMessage = 'Facility ID is missing.';
      return;
    }

    this.isFacilityLoading = true;
    this.facilityErrorMessage = '';

    this.facilityService.getFacilityDetails(this.facilityId)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (res) => {
          this.isFacilityLoading = false;
          const data = (res && res.data) ? res.data : (res && res.facility ? res.facility : res);
          if (data) {
            this.mapFacilityCard(data);
          } else {
            this.facilityErrorMessage = 'No facility details found.';
          }
        },
        (error) => {
          console.error('Error fetching facility details:', error);
          this.isFacilityLoading = false;
          this.facilityErrorMessage = 'Failed to load facility details.';
        }
      );
  }

  mapFacilityCard(data: any): void {
    const img = data.e_facility_media && data.e_facility_media[0] ? data.e_facility_media[0].image : null;
    this.facilityCard = {
      id: data.id,
      name: data.name || '—',
      address: data.street_address || '—',
      cityName: data.e_cities ? data.e_cities.name : '',
      stateName: data.e_states ? data.e_states.name : '',
      image: img,
      personCount: data.capacity || 0,
      bedCount: data.bedrooms || 0,
      bathCount: data.bathrooms || 0,
      squareFootage: data.area || 0
    };
  }

  get form() {
    return this.ratingForm.controls;
  }

  getRatingValue(key: string): number {
    if (!this.ratingForm) {
      return 0;
    }
    const control = this.ratingForm.get(key);
    return control ? control.value : 0;
  }

  setRating(key: string, val: number): void {
    if (!this.ratingForm) {
      return;
    }
    const control = this.ratingForm.get(key);
    if (control) {
      control.setValue(val);
    }
  }

  closeModal(): void {
    this.onCloseModal.emit();
  }

  onSubmitReview(): void {
    this.submitted = true;
    if (this.ratingForm.invalid) {
      return;
    }

    if (!this.referenceId) {
      this.toastr.error('Reservation Reference ID is missing.');
      return;
    }

    const payload = {
      ratings: [
        Number(this.ratingForm.get('ratingFacility').value),
        Number(this.ratingForm.get('ratingCleanliness').value),
        Number(this.ratingForm.get('ratingValueForMoney').value),
        Number(this.ratingForm.get('ratingRoomComfort').value),
        Number(this.ratingForm.get('ratingStaffCare').value)
      ],
      title: this.ratingForm.get('title').value,
      description: this.ratingForm.get('summary').value
    };

    // Validation check: ratings must be between 1 and 5
    for (let i = 0; i < payload.ratings.length; i++) {
      if (payload.ratings[i] < 1 || payload.ratings[i] > 5) {
        this.toastr.error('Each rating category must be between 1 and 5 stars.');
        return;
      }
    }

    if (this.isSubmitting) {
      return;
    }
    this.isSubmitting = true;

    this.reservationService.submitReview(this.referenceId, payload)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (res) => {
          this.isSubmitting = false;
          this.toastr.success('Review submitted successfully.');
          this.closeModal();
        },
        (error) => {
          this.isSubmitting = false;
          console.error('Error submitting review:', error);
          let errorMsg = 'Failed to submit review.';
          if (typeof error === 'string') {
            errorMsg = error;
          } else if (error && error.error && error.error.message) {
            errorMsg = error.error.message;
          } else if (error && error.message) {
            errorMsg = error.message;
          }
          this.toastr.error(errorMsg);
        }
      );
  }
}
